import { useMemo, useState } from 'react'
import { useEnrollmentStore } from '@/stores'
import { isKnownUsState, normalizeStateCode } from '../states'
import { getEnrollmentString } from '../../../utils/enrollmentHydration/helpers'
import type { EligibilityFormData } from '../types'
import {
  formatLicenseNumber,
  getLicenseValidationError,
  getTodayForDatePicker,
  toStoredDate
} from './helpers'
import { useLicenseDocumentUpload } from './useLicenseDocumentUpload'

type UseLicenseDetailsStepArgs = {
  formData: EligibilityFormData
  updateFormData: (data: Partial<EligibilityFormData>) => void
  onContinue: () => void
}

export function useLicenseDetailsStep({
  formData,
  updateFormData,
  onContinue
}: UseLicenseDetailsStepArgs) {
  const [validationError, setValidationError] = useState<string | null>(null)
  const storedFormAddressState = useEnrollmentStore(state => state.formData.state)
  const resumedEnrollment = useEnrollmentStore(state => state.resumedEnrollment)
  const userProfile = useEnrollmentStore(state => state.userProfile)
  const storedAddressState = normalizeStateCode(
    [
      getEnrollmentString(resumedEnrollment ?? null, ['state']),
      userProfile?.personalData?.state,
      storedFormAddressState
    ].find(isKnownUsState)
  )
  const selectedLicenseState = normalizeStateCode(formData.driverLicenseState)
  const today = getTodayForDatePicker()

  const licenseStateMismatchWarning = useMemo(() => {
    if (!storedAddressState || !selectedLicenseState) {
      return undefined
    }

    if (storedAddressState === selectedLicenseState) {
      return undefined
    }

    return "Driver's license state must match your address state."
  }, [selectedLicenseState, storedAddressState])

  const {
    licenseUploadInputRef,
    uploadedLicenseFileName,
    uploadError,
    isUploading,
    setUploadError,
    handleDriverLicenseFileChange
  } = useLicenseDocumentUpload({
    driverLicenseNumber: formData.driverLicenseNumber,
    driverLicenseState: formData.driverLicenseState,
    licenseExpirationDate: formData.licenseExpirationDate
  })

  const clearStepErrors = () => {
    setValidationError(null)
    setUploadError(null)
  }

  const handleDriverLicenseUploadClick = () => {
    clearStepErrors()
    licenseUploadInputRef.current?.click()
  }

  const handleDriverLicenseStateChange = (value: string) => {
    setValidationError(null)
    updateFormData({ driverLicenseState: value })
  }

  const handleDriverLicenseNumberChange = (value: string) => {
    setValidationError(null)
    updateFormData({ driverLicenseNumber: value })
  }

  const handleIssueDateChange = (value: string) => {
    setValidationError(null)
    updateFormData({ licenseIssueDate: toStoredDate(value) })
  }

  const handleExpirationDateChange = (value: string) => {
    setValidationError(null)
    updateFormData({ licenseExpirationDate: toStoredDate(value) })
  }

  const handleContinueClick = () => {
    if (licenseStateMismatchWarning) {
      return
    }

    const formattedLicenseNumber = formatLicenseNumber(formData.driverLicenseNumber)
    const nextValidationError = getLicenseValidationError(formData, uploadedLicenseFileName)

    if (nextValidationError) {
      setValidationError(nextValidationError)
      return
    }

    setValidationError(null)

    if (formattedLicenseNumber !== formData.driverLicenseNumber) {
      updateFormData({ driverLicenseNumber: formattedLicenseNumber })
    }

    onContinue()
  }

  const blockingValidationError = getLicenseValidationError(formData, uploadedLicenseFileName)

  const isContinueDisabled =
    Boolean(licenseStateMismatchWarning) ||
    Boolean(blockingValidationError) ||
    Boolean(uploadError) ||
    isUploading

  return {
    handleContinueClick,
    handleDriverLicenseFileChange,
    handleDriverLicenseNumberChange,
    handleDriverLicenseStateChange,
    handleDriverLicenseUploadClick,
    handleExpirationDateChange,
    handleIssueDateChange,
    isContinueDisabled,
    isUploading,
    licenseStateMismatchWarning,
    licenseUploadInputRef,
    today,
    uploadError,
    uploadedLicenseFileName,
    validationError
  }
}
